import type { MetaFunction } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { Link } from 'react-router'
import { ArrowRight, CheckCircle, Hammer, Droplets, ChefHat, Thermometer, Paintbrush, Home, Zap, Ruler } from 'lucide-react'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { FAQSchema, BreadcrumbSchema, StructuredData } from '~/components/structured-data'

const CALC_FAQS = [
  { question: "Comment fonctionne le calculateur de prix travaux ?", answer: "Choisissez le type de travaux (rénovation au m², salle de bain, cuisine, isolation, etc.), renseignez la surface et les caractéristiques de votre projet, et le calculateur affiche une fourchette de prix instantanée basée sur les tarifs moyens 2026 du marché français." },
  { question: "Les estimations sont-elles précises ?", answer: "Les calculateurs donnent une fourchette indicative basée sur les tarifs constatés en 2026 (sources : ADEME, France Rénov', études marché). Le prix final peut varier de ±15 % selon l'état du bien, l'accessibilité, les matériaux choisis et la région. Pour un chiffrage exact, demandez 3 devis gratuits." },
  { question: "Le calculateur prend-il en compte les aides financières ?", answer: "Le calculateur PAC, isolation et fenêtres affiche le montant des aides MaPrimeRénov', CEE et éco-PTZ déductibles selon votre profil. Les autres calculateurs affichent le prix HT/TTC, hors aides — utilisez le simulateur d'aides pour cumuler les dispositifs." },
  { question: "Puis-je utiliser le calculateur sans inscription ?", answer: "Oui, tous les calculateurs sont 100 % gratuits et sans inscription. Vous pouvez les utiliser autant de fois que vous voulez. Pour recevoir 3 devis comparatifs réels, il suffit de cliquer sur le CTA en bas du calculateur." },
]

const CALCULATOR_TYPES = [
  { id: 'renovation-m2', title: 'Rénovation au m²', desc: 'Estimez le coût d\'une rénovation complète selon la surface', icon: Hammer, color: 'bg-orange-50 text-orange-600' },
  { id: 'salle-de-bain', title: 'Salle de bain', desc: 'Création ou rénovation complète de salle de bain', icon: Droplets, color: 'bg-blue-50 text-blue-600' },
  { id: 'cuisine', title: 'Cuisine', desc: 'Installation cuisine équipée ou sur mesure', icon: ChefHat, color: 'bg-amber-50 text-amber-600' },
  { id: 'isolation', title: 'Isolation thermique', desc: 'ITE, ITI, combles — estimation avec aides', icon: Thermometer, color: 'bg-emerald-50 text-emerald-600' },
  { id: 'peinture', title: 'Peinture', desc: 'Coût peinture intérieure ou extérieure au m²', icon: Paintbrush, color: 'bg-pink-50 text-pink-600' },
  { id: 'toiture', title: 'Toiture', desc: 'Réfection, isolation ou réparation toiture', icon: Home, color: 'bg-red-50 text-red-600' },
  { id: 'fenetres', title: 'Fenêtres', desc: 'Remplacement double vitrage + aides MaPrimeRénov\'', icon: Ruler, color: 'bg-cyan-50 text-cyan-600' },
  { id: 'pac', title: 'Pompe à chaleur', desc: 'Installation PAC air-eau ou air-air + aides', icon: Zap, color: 'bg-indigo-50 text-indigo-600' },
]

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Calculateur de prix travaux 2026 — Estimation gratuite | AfficheServices' },
  { name: 'description', content: 'Estimez le coût de vos travaux en 30 secondes : peinture, carrelage, rénovation, plomberie, électricité. Prix du marché 2026, calcul instantané et gratuit.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/calculateur' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Calculateur+prix&subtitle=Estimez+vos+travaux' },
]

export default function CalculateurPage() {
  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Calculateurs', url: 'https://www.afficheservices.fr/calculateur' },
      ]} />
      <FAQSchema items={CALC_FAQS} />
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Calculateur de prix travaux AfficheServices',
        url: 'https://www.afficheservices.fr/calculateur',
        applicationCategory: 'FinanceApplication',
        operatingSystem: 'All (web-based)',
        description: 'Suite de 8 calculateurs gratuits pour estimer le coût de vos travaux : rénovation, salle de bain, cuisine, isolation, peinture, toiture, fenêtres, pompe à chaleur. Données 2026 actualisées.',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
        featureList: CALCULATOR_TYPES.map((c) => `${c.title} : ${c.desc}`),
      }} />

      <div className="mx-auto max-w-3xl px-4 py-12 lg:px-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">
            Calculateur de prix travaux
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            Estimez le coût de vos travaux en 30 secondes.
            Prix basés sur les tarifs moyens du marché français 2026.
          </p>
        </div>

        {/* Type chooser — wizard entry point */}
        <div className="mt-8">
          <div className="text-sm font-semibold text-slate-700">Que voulez-vous calculer ?</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {CALCULATOR_TYPES.map((calc) => (
              <Link
                key={calc.id}
                to={`/calculateur/${calc.id}`}
                className="group flex flex-col rounded-2xl border border-border bg-white p-5 shadow-xs transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
              >
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${calc.color}`}>
                  <calc.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-3 text-sm font-bold text-slate-900 group-hover:text-primary">{calc.title}</h3>
                <p className="mt-1 text-xs text-slate-500">{calc.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  Commencer <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Trust signals */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-400">
          <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-500" /> Estimation gratuite</span>
          <span>·</span>
          <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-500" /> Prix du marché 2026</span>
          <span>·</span>
          <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-500" /> Sans inscription</span>
        </div>

        {/* SEO content */}
        <div className="mt-12 space-y-6">
          <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Comment utiliser notre calculateur ?</h2>
            <ol className="mt-4 space-y-3 text-sm text-slate-600">
              <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">1</span> Sélectionnez le type de travaux (peinture, carrelage, rénovation...)</li>
              <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">2</span> Choisissez le niveau de finition si applicable</li>
              <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">3</span> Indiquez la surface ou la quantité</li>
              <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">4</span> Obtenez une estimation instantanée basée sur les prix du marché</li>
            </ol>
          </div>

          <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Prix moyens des travaux en France (2026)</h2>
            <div className="mt-4 overflow-hidden rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
                    <th className="px-4 py-2.5">Travaux</th>
                    <th className="px-4 py-2.5 text-right">Prix min</th>
                    <th className="px-4 py-2.5 text-right">Prix max</th>
                    <th className="px-4 py-2.5 text-right">Unité</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-sm">
                  {[
                    { name: 'Peinture intérieure', min: '20 €', max: '45 €', unit: '/m²' },
                    { name: 'Carrelage sol', min: '25 €', max: '70 €', unit: '/m²' },
                    { name: 'Rénovation salle de bain', min: '3 000 €', max: '15 000 €', unit: '/pièce' },
                    { name: 'Plomberie (dépannage)', min: '80 €', max: '200 €', unit: '/inter.' },
                    { name: 'Électricité (installation)', min: '80 €', max: '150 €', unit: '/point' },
                    { name: 'Isolation murs (intérieur)', min: '30 €', max: '80 €', unit: '/m²' },
                    { name: 'Toiture (réparation)', min: '40 €', max: '120 €', unit: '/m²' },
                    { name: 'Façade (ravalement)', min: '30 €', max: '80 €', unit: '/m²' },
                  ].map((row) => (
                    <tr key={row.name} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-slate-900">{row.name}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-slate-900">{row.min}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-slate-900">{row.max}</td>
                      <td className="px-4 py-2.5 text-right text-slate-400">{row.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-400">* Prix indicatifs TTC, main d'œuvre comprise. Mars 2026.</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 rounded-2xl bg-slate-900 p-8 text-center">
          <h2 className="text-xl font-bold text-white">Besoin d'un prix exact ?</h2>
          <p className="mt-2 text-sm text-slate-400">Recevez 3 devis personnalisés de pros vérifiés</p>
          <Link
            to="/demande-devis/1"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press"
          >
            Devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  )
}
